function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import { useHistory, Link } from 'react-router-dom';
import compact from 'lodash/compact';
import { TableList, Typography, TableListActions, DropdownMenu } from 'seagull';
import { EmailFill, CloseFill, CopyFill, EditFill, LinkIcon, EyeFill } from 'seagull/icons';
import { useTerminateAgreement } from "../../hooks/useTerminateAgreement";
import { useWithdrawToEditProposal } from "../../hooks/useWithdrawToEditProposal";
import { useCopyProposalLink } from "../../hooks/useCopyProposalLink";
import { useDuplicateRelationship } from "../../hooks/useDuplicateRelationship";
import { useEventReporter, useGlobalContext } from "../../contexts";
import { routes } from "../../constants";
import { Intent } from "../../infra/access-control";
import { ClientPreview } from "../../components/ClientPreview/ClientPreview";
import { getStatusUIData, getIsStatusMicroDepositFailed } from "./ProposalsDashboard.utils";
import styles from "./ProposalsDashboardRow.module.scss";
import { relationshipSummaryModel } from "../../models";
import { COBStep } from "../../client-flow/clientOb/ClientOnboarding.utils";
import MissingSignaturesReminderModal from "./MissingSignaturesReminderModal/MissingSignaturesReminderModal";
export default function ProposalsDashboardRow(props) {
  const {
    agreement,
    isVendor,
    index,
    refresh,
    onOpenReview
  } = props;
  const history = useHistory();
  const {
    cancelProposalById
  } = useTerminateAgreement();
  const {
    duplicateRelationshipAndRedirect
  } = useDuplicateRelationship();
  const {
    withdrawToEditProposalByIdWithConfirmation
  } = useWithdrawToEditProposal();
  const {
    copyAgreementLink
  } = useCopyProposalLink();
  const {
    reportToGtm,
    reportToAppcues
  } = useEventReporter();
  const globalContext = useGlobalContext();
  const heapId = agreement.isSample ? 'test-proposal' : 'proposals';
  const mainActionIntent = isVendor ? Intent.VendorViewProposal : Intent.ClientViewProposal;
  const opposingPartyName = relationshipSummaryModel.opposingPartyName(agreement, isVendor);
  const {
    statusText,
    statusSkin,
    lastUpdateText,
    allowedActions
  } = getStatusUIData(agreement, isVendor);
  const shouldShowWithdrawToEdit = allowedActions === null || allowedActions === void 0 ? void 0 : allowedActions.withdrawToEdit;
  const shouldShowReminder = allowedActions === null || allowedActions === void 0 ? void 0 : allowedActions.reminder;
  const shouldShowDuplicate = allowedActions === null || allowedActions === void 0 ? void 0 : allowedActions.duplicate;
  const shouldShowCancel = allowedActions === null || allowedActions === void 0 ? void 0 : allowedActions.cancel;
  const shouldShowCopyLink = allowedActions === null || allowedActions === void 0 ? void 0 : allowedActions.copyLink;
  const shouldShowActions = shouldShowWithdrawToEdit && shouldShowReminder && shouldShowDuplicate && shouldShowCancel;
  const clientAgreementLink = routes.clientOnboarding(agreement.id, getIsStatusMicroDepositFailed(agreement) ? COBStep.paymentMethod : undefined);
  const openMissingSignaturesReminderModal = () => {
    globalContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(MissingSignaturesReminderModal, {
          dismissModal: dismissModal,
          relationshipId: agreement.id
        });
      }
    });
  };
  const actions = compact([shouldShowCopyLink && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    intent: Intent.CopyRelationshipDirectLink,
    onClick: () => {
      reportToGtm('button-click', 'vendor-copy-link-proposal', {
        progressStatus: agreement.progressStatus
      });
      reportToAppcues('button-click', 'vendor-copy-link-proposal');
      copyAgreementLink(agreement.id, agreement.vendor.id, agreement.customer.id, agreement.progressStatus);
    },
    iconStart: /*#__PURE__*/React.createElement(LinkIcon, null),
    "data-testid": agreement.isSample ? 'test-proposal-copy-link' : 'proposals-row-copy-link',
    key: "copy-link-".concat(agreement.id)
  }, "Copy link to proposal"), shouldShowReminder && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    intent: Intent.SendProposalReminder,
    onClick: openMissingSignaturesReminderModal,
    iconStart: /*#__PURE__*/React.createElement("div", {
      className: styles.iconWrapper
    }, /*#__PURE__*/React.createElement(EmailFill, null)),
    disabled: agreement.isSample,
    "data-testid": "proposals-row-send-reminder",
    key: "reminder".concat(agreement.id)
  }, "Send reminder"), isVendor && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    onClick: () => {
      globalContext.showModal({
        closeOnClickOutside: true,
        closeOnEscapeKey: true,
        content: props => /*#__PURE__*/React.createElement(ClientPreview, _extends({
          type: "relationshipId",
          relationshipId: agreement.id
        }, props))
      });
    },
    iconStart: /*#__PURE__*/React.createElement("div", {
      className: styles.iconWrapper
    }, /*#__PURE__*/React.createElement(EyeFill, null)),
    "data-testid": "proposals-row-view-proposal",
    key: "view-proposal".concat(agreement.id)
  }, "View proposal"), shouldShowWithdrawToEdit && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    intent: Intent.EditProposal,
    onClick: () => withdrawToEditProposalByIdWithConfirmation(agreement.id),
    iconStart: /*#__PURE__*/React.createElement(EditFill, null),
    disabled: agreement.isSample,
    "data-testid": "proposals-row-withdraw-to-edit",
    key: "withdraw-to-edit".concat(agreement.id)
  }, "Edit proposal"), shouldShowDuplicate && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    intent: Intent.DuplicateProposal,
    iconStart: /*#__PURE__*/React.createElement(CopyFill, null),
    disabled: agreement.isSample,
    "data-testid": "proposals-row-duplicate",
    onClick: () => duplicateRelationshipAndRedirect(agreement.id, 'duplicate-proposal'),
    key: "duplicate".concat(agreement.id)
  }, "Duplicate proposal"), shouldShowActions && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    intent: Intent.CancelProposal,
    iconStart: /*#__PURE__*/React.createElement("div", {
      className: styles.iconWrapper
    }, /*#__PURE__*/React.createElement(CloseFill, null)),
    "data-testid": agreement.isSample ? 'test-proposal-cancel' : 'proposals-row-cancel',
    onClick: () => cancelProposalById(agreement.id, refresh),
    key: "cancel".concat(agreement.id)
  }, "Cancel proposal")]);
  function onView() {
    if (isVendor) {
      onOpenReview(agreement);
    } else {
      history.push(clientAgreementLink);
    }
  }
  return /*#__PURE__*/React.createElement(TableList.Row, {
    key: agreement.id,
    intent: mainActionIntent,
    as: isVendor ? 'button' : Link,
    to: isVendor ? undefined : clientAgreementLink,
    onClick: onView,
    rowId: agreement.id,
    items: [{
      value: /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        weight: "normal",
        "data-testid": "proposals-row-".concat(index, "-name")
      }, opposingPartyName, agreement.title && agreement.title !== opposingPartyName && /*#__PURE__*/React.createElement(Typography, {
        as: "span",
        variant: "paragraph2",
        skin: "secondary"
      }, "\xA0(", agreement.title, ")"))
    }, {
      value: /*#__PURE__*/React.createElement(Typography, {
        skin: "secondary"
      }, agreement !== null && agreement !== void 0 && agreement.createdOn ? new Date(agreement.createdOn).toLocaleString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      }) : '')
    }, {
      value: (agreement === null || agreement === void 0 ? void 0 : agreement.seller.firstName) != null && /*#__PURE__*/React.createElement(Typography, {
        skin: "secondary"
      }, "".concat(agreement === null || agreement === void 0 ? void 0 : agreement.seller.firstName, " ").concat(agreement === null || agreement === void 0 ? void 0 : agreement.seller.lastName))
    }, {
      value: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "row-".concat(index, "-status"),
        "data-skin": statusSkin,
        skin: statusSkin,
        weight: "normal"
      }, statusText), /*#__PURE__*/React.createElement(Typography, {
        variant: "paragraph2",
        size: "small",
        "data-testid": "row-".concat(index, "-status-update"),
        "data-skin": statusSkin,
        skin: statusSkin === 'error' ? 'error' : 'secondary'
      }, lastUpdateText))
    }],
    actionColumn: /*#__PURE__*/React.createElement(TableListActions, {
      buttonText: isVendor ? 'Review' : 'View',
      buttonIntent: mainActionIntent,
      testId: agreement.isSample ? 'test-proposal' : "proposals-row-".concat(index),
      btnHeapId: "".concat(heapId, "-").concat(isVendor ? 'review' : 'view', "-btn"),
      menuHeapId: "".concat(heapId, "-menu-btn"),
      onClickButton: onView
    }, shouldShowActions && actions),
    "data-testid": agreement.isSample ? 'test-proposal' : "proposals-row-".concat(index),
    "data-heapid": heapId
  });
}